import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const initialFilters = {
  search: "",
  paymentType: "",
  direction: "",
  paymentMethod: "",
  status: "",

  customerId: "",
  branchId: "",
  firmId: "",

  fromDate: "",
  toDate: "",

  sortBy: "paymentDate",
  sortOrder: "desc",

  page: 1,
  limit: 20,
};

const initialPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const initialSummary = {
  totalCount: 0,
  totalAmount: 0,
  receivedAmount: 0,
  refundedAmount: 0,
  allocatedAmount: 0,
  unallocatedAmount: 0,

  cashAmount: 0,
  upiAmount: 0,
  cardAmount: 0,
  bankTransferAmount: 0,
  chequeAmount: 0,
  gatewayAmount: 0,

  completedCount: 0,
  pendingCount: 0,
  cancelledCount: 0,
};

const initialState = {
  payments: [],
  selectedPayment: null,

  outstandingInvoices: [],
  outstandingSummary: {
    invoiceCount: 0,
    totalInvoiceAmount: 0,
    paidAmount: 0,
    outstandingAmount: 0,
  },

  summary: {
    ...initialSummary,
  },

  createData: null,

  dropdowns: {
    paymentTypes: [],
    directions: [],
    paymentMethods: [],
    statuses: [],
    accountTypes: [],
  },

  filters: {
    ...initialFilters,
  },

  pagination: {
    ...initialPagination,
  },

  listLoading: false,
  detailsLoading: false,
  summaryLoading: false,
  createDataLoading: false,
  dropdownsLoading: false,
  outstandingLoading: false,

  createLoading: false,
  updateLoading: false,
  cancelLoading: false,
  deleteLoading: false,

  error: null,
  actionError: null,

  operationSuccess: false,
  operationMessage: "",

  lastCreatedPayment: null,
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response,
  fallback = {}
) => {
  if (!response) {
    return fallback;
  }

  return (
    response.data ||
    response.result ||
    response
  );
};

const getErrorMessage = (
  response,
  fallbackMessage
) => {
  return (
    response?.message ||
    response?.error?.message ||
    response?.error ||
    fallbackMessage
  );
};

const rejectIfFailed = (
  response,
  rejectWithValue,
  fallbackMessage
) => {
  if (
    !response ||
    response.success === false ||
    response.status === "error"
  ) {
    return rejectWithValue(
      getErrorMessage(
        response,
        fallbackMessage
      )
    );
  }

  return null;
};

const extractPayment = (
  payload
) => {
  return (
    payload?.payment ||
    payload?.salesPayment ||
    payload
  );
};

const updatePaymentInList = (
  state,
  payment
) => {
  if (!payment?._id) {
    return;
  }

  const index =
    state.payments.findIndex(
      (item) =>
        item._id ===
        payment._id
    );

  if (index >= 0) {
    state.payments[index] =
      payment;
  }
};

/*
|--------------------------------------------------------------------------
| FETCH PAYMENT LIST
|--------------------------------------------------------------------------
*/

export const fetchBillingPayments =
  createAsyncThunk(
    "billingPayment/fetchBillingPayments",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const filters =
        getState()
          ?.billingPayment
          ?.filters ||
        initialFilters;

      const response =
        await api.get(
          config.getBillingPayments,
          {
            ...filters,
            ...params,
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing payments."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchBillingPaymentSummary =
  createAsyncThunk(
    "billingPayment/fetchBillingPaymentSummary",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const filters =
        getState()
          ?.billingPayment
          ?.filters ||
        initialFilters;

      const response =
        await api.get(
          config.getBillingPaymentSummary,
          {
            ...filters,
            ...params,

            page:
              undefined,

            limit:
              undefined,

            sortBy:
              undefined,

            sortOrder:
              undefined,
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch payment summary."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH CREATE DATA
|--------------------------------------------------------------------------
*/

export const fetchBillingPaymentCreateData =
  createAsyncThunk(
    "billingPayment/fetchBillingPaymentCreateData",
    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingPaymentCreateData,
          params
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch payment create data."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH DROPDOWNS
|--------------------------------------------------------------------------
*/

export const fetchBillingPaymentDropdowns =
  createAsyncThunk(
    "billingPayment/fetchBillingPaymentDropdowns",
    async (
      _,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingPaymentDropdowns
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch payment dropdowns."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH PAYMENT BY ID
|--------------------------------------------------------------------------
*/

export const fetchBillingPaymentById =
  createAsyncThunk(
    "billingPayment/fetchBillingPaymentById",
    async (
      paymentId,
      {
        rejectWithValue,
      }
    ) => {
      if (!paymentId) {
        return rejectWithValue(
          "Payment ID is required."
        );
      }

      const response =
        await api.get(
          config.getBillingPaymentById(
            paymentId
          )
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch payment details."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH CUSTOMER OUTSTANDING INVOICES
|--------------------------------------------------------------------------
*/

export const fetchCustomerOutstandingInvoices =
  createAsyncThunk(
    "billingPayment/fetchCustomerOutstandingInvoices",
    async (
      {
        customerId,
        params = {},
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!customerId) {
        return rejectWithValue(
          "Customer ID is required."
        );
      }

      const response =
        await api.get(
          config.getCustomerOutstandingInvoices(
            customerId
          ),
          params
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch outstanding invoices."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| CREATE PAYMENT
|--------------------------------------------------------------------------
*/

export const createBillingPayment =
  createAsyncThunk(
    "billingPayment/createBillingPayment",
    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.post(
          config.createBillingPayment,
          payload || {}
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to create payment."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Payment created successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| UPDATE PAYMENT
|--------------------------------------------------------------------------
*/

export const updateBillingPayment =
  createAsyncThunk(
    "billingPayment/updateBillingPayment",
    async (
      {
        paymentId,
        payload,
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!paymentId) {
        return rejectWithValue(
          "Payment ID is required."
        );
      }

      const response =
        await api.put(
          config.updateBillingPayment(
            paymentId
          ),
          payload || {}
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to update payment."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Payment updated successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| CANCEL PAYMENT
|--------------------------------------------------------------------------
*/

export const cancelBillingPayment =
  createAsyncThunk(
    "billingPayment/cancelBillingPayment",
    async (
      {
        paymentId,
        reason,
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!paymentId) {
        return rejectWithValue(
          "Payment ID is required."
        );
      }

      if (!reason?.trim()) {
        return rejectWithValue(
          "Cancellation reason is required."
        );
      }

      const response =
        await api.patch(
          config.cancelBillingPayment(
            paymentId
          ),
          {
            reason:
              reason.trim(),
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to cancel payment."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Payment cancelled successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| DELETE PAYMENT
|--------------------------------------------------------------------------
*/

export const deleteBillingPayment =
  createAsyncThunk(
    "billingPayment/deleteBillingPayment",
    async (
      paymentId,
      {
        rejectWithValue,
      }
    ) => {
      if (!paymentId) {
        return rejectWithValue(
          "Payment ID is required."
        );
      }

      const response =
        await api.delete(
          config.deleteBillingPayment(
            paymentId
          )
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to delete payment."
        );

      if (failed) {
        return failed;
      }

      return {
        paymentId,

        message:
          response?.message ||
          "Payment deleted successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const paymentSlice =
  createSlice({
    name:
      "billingPayment",

    initialState,

    reducers: {
      setPaymentFilters: (
        state,
        action
      ) => {
        state.filters = {
          ...state.filters,
          ...(action.payload || {}),
        };
      },

      setPaymentFilter: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload || {};

        if (!key) {
          return;
        }

        state.filters[key] =
          value;

        if (
          key !== "page" &&
          key !== "limit"
        ) {
          state.filters.page = 1;
        }
      },

      setPaymentPage: (
        state,
        action
      ) => {
        state.filters.page =
          Number(
            action.payload
          ) || 1;
      },

      setPaymentLimit: (
        state,
        action
      ) => {
        state.filters.limit =
          Number(
            action.payload
          ) || 20;

        state.filters.page = 1;
      },

      resetPaymentFilters: (
        state
      ) => {
        state.filters = {
          ...initialFilters,
        };
      },

      setSelectedPayment: (
        state,
        action
      ) => {
        state.selectedPayment =
          action.payload || null;
      },

      clearSelectedPayment: (
        state
      ) => {
        state.selectedPayment =
          null;
      },

      clearOutstandingInvoices: (
        state
      ) => {
        state.outstandingInvoices =
          [];

        state.outstandingSummary = {
          invoiceCount: 0,
          totalInvoiceAmount: 0,
          paidAmount: 0,
          outstandingAmount: 0,
        };
      },

      clearPaymentErrors: (
        state
      ) => {
        state.error = null;
        state.actionError = null;
      },

      clearPaymentOperation: (
        state
      ) => {
        state.operationSuccess =
          false;

        state.operationMessage =
          "";

        state.actionError =
          null;
      },

      clearLastCreatedPayment: (
        state
      ) => {
        state.lastCreatedPayment =
          null;
      },

      resetPaymentState:
        () => ({
          ...initialState,

          filters: {
            ...initialFilters,
          },

          pagination: {
            ...initialPagination,
          },

          summary: {
            ...initialSummary,
          },
        }),
    },

    extraReducers: (
      builder
    ) => {
      /*
      |--------------------------------------------------------------------------
      | LIST
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingPayments.pending,
          (state) => {
            state.listLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingPayments.fulfilled,
          (
            state,
            action
          ) => {
            state.listLoading =
              false;

            const payload =
              action.payload || {};

            state.payments =
              payload.payments ||
              payload.salesPayments ||
              payload.records ||
              payload.items ||
              [];

            state.pagination = {
              ...initialPagination,
              ...(payload.pagination ||
                {}),
            };
          }
        )
        .addCase(
          fetchBillingPayments.rejected,
          (
            state,
            action
          ) => {
            state.listLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch billing payments.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SUMMARY
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingPaymentSummary.pending,
          (state) => {
            state.summaryLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingPaymentSummary.fulfilled,
          (
            state,
            action
          ) => {
            state.summaryLoading =
              false;

            const payload =
              action.payload || {};

            state.summary = {
              ...initialSummary,
              ...(payload.summary ||
                payload),
            };
          }
        )
        .addCase(
          fetchBillingPaymentSummary.rejected,
          (
            state,
            action
          ) => {
            state.summaryLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch payment summary.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | CREATE DATA
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingPaymentCreateData.pending,
          (state) => {
            state.createDataLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingPaymentCreateData.fulfilled,
          (
            state,
            action
          ) => {
            state.createDataLoading =
              false;

            state.createData =
              action.payload ||
              null;
          }
        )
        .addCase(
          fetchBillingPaymentCreateData.rejected,
          (
            state,
            action
          ) => {
            state.createDataLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch payment create data.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DROPDOWNS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingPaymentDropdowns.pending,
          (state) => {
            state.dropdownsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingPaymentDropdowns.fulfilled,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            const payload =
              action.payload || {};

            state.dropdowns = {
              ...state.dropdowns,
              ...(payload.dropdowns ||
                payload),
            };
          }
        )
        .addCase(
          fetchBillingPaymentDropdowns.rejected,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch payment dropdowns.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DETAILS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingPaymentById.pending,
          (state) => {
            state.detailsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingPaymentById.fulfilled,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;

            state.selectedPayment =
              extractPayment(
                action.payload
              );
          }
        )
        .addCase(
          fetchBillingPaymentById.rejected,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch payment details.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | OUTSTANDING
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchCustomerOutstandingInvoices.pending,
          (state) => {
            state.outstandingLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchCustomerOutstandingInvoices.fulfilled,
          (
            state,
            action
          ) => {
            state.outstandingLoading =
              false;

            const payload =
              action.payload || {};

            state.outstandingInvoices =
              payload.invoices ||
              payload.documents ||
              payload.records ||
              [];

            state.outstandingSummary = {
              ...state.outstandingSummary,
              ...(payload.summary ||
                {}),
            };
          }
        )
        .addCase(
          fetchCustomerOutstandingInvoices.rejected,
          (
            state,
            action
          ) => {
            state.outstandingLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Unable to fetch outstanding invoices.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | CREATE
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          createBillingPayment.pending,
          (state) => {
            state.createLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          createBillingPayment.fulfilled,
          (
            state,
            action
          ) => {
            state.createLoading =
              false;

            const payment =
              extractPayment(
                action.payload
                  ?.data
              );

            state.lastCreatedPayment =
              payment;

            state.selectedPayment =
              payment;

            if (payment?._id) {
              state.payments.unshift(
                payment
              );
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Payment created successfully.";
          }
        )
        .addCase(
          createBillingPayment.rejected,
          (
            state,
            action
          ) => {
            state.createLoading =
              false;

            state.actionError =
              action.payload ||
              action.error
                ?.message ||
              "Unable to create payment.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | UPDATE
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          updateBillingPayment.pending,
          (state) => {
            state.updateLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          updateBillingPayment.fulfilled,
          (
            state,
            action
          ) => {
            state.updateLoading =
              false;

            const payment =
              extractPayment(
                action.payload
                  ?.data
              );

            state.selectedPayment =
              payment;

            updatePaymentInList(
              state,
              payment
            );

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Payment updated successfully.";
          }
        )
        .addCase(
          updateBillingPayment.rejected,
          (
            state,
            action
          ) => {
            state.updateLoading =
              false;

            state.actionError =
              action.payload ||
              action.error
                ?.message ||
              "Unable to update payment.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | CANCEL
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          cancelBillingPayment.pending,
          (state) => {
            state.cancelLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          cancelBillingPayment.fulfilled,
          (
            state,
            action
          ) => {
            state.cancelLoading =
              false;

            const payment =
              extractPayment(
                action.payload
                  ?.data
              );

            state.selectedPayment =
              payment;

            updatePaymentInList(
              state,
              payment
            );

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Payment cancelled successfully.";
          }
        )
        .addCase(
          cancelBillingPayment.rejected,
          (
            state,
            action
          ) => {
            state.cancelLoading =
              false;

            state.actionError =
              action.payload ||
              action.error
                ?.message ||
              "Unable to cancel payment.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DELETE
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          deleteBillingPayment.pending,
          (state) => {
            state.deleteLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          deleteBillingPayment.fulfilled,
          (
            state,
            action
          ) => {
            state.deleteLoading =
              false;

            const paymentId =
              action.payload
                ?.paymentId;

            state.payments =
              state.payments.filter(
                (payment) =>
                  payment._id !==
                  paymentId
              );

            if (
              state
                .selectedPayment
                ?._id ===
              paymentId
            ) {
              state.selectedPayment =
                null;
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Payment deleted successfully.";
          }
        )
        .addCase(
          deleteBillingPayment.rejected,
          (
            state,
            action
          ) => {
            state.deleteLoading =
              false;

            state.actionError =
              action.payload ||
              action.error
                ?.message ||
              "Unable to delete payment.";
          }
        );
    },
  });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  setPaymentFilters,
  setPaymentFilter,
  setPaymentPage,
  setPaymentLimit,
  resetPaymentFilters,

  setSelectedPayment,
  clearSelectedPayment,

  clearOutstandingInvoices,
  clearPaymentErrors,
  clearPaymentOperation,
  clearLastCreatedPayment,

  resetPaymentState,
} = paymentSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const billingPaymentSelector = (
  state
) => state.billingPayment;

export const billingPaymentListSelector = (
  state
) =>
  state.billingPayment.payments;

export const selectedBillingPaymentSelector = (
  state
) =>
  state.billingPayment
    .selectedPayment;

export const billingPaymentSummarySelector = (
  state
) =>
  state.billingPayment.summary;

export const billingPaymentFiltersSelector = (
  state
) =>
  state.billingPayment.filters;

export const billingPaymentPaginationSelector = (
  state
) =>
  state.billingPayment.pagination;

export const billingPaymentCreateDataSelector = (
  state
) =>
  state.billingPayment.createData;

export const billingPaymentDropdownsSelector = (
  state
) =>
  state.billingPayment.dropdowns;

export const customerOutstandingInvoicesSelector = (
  state
) => ({
    invoices:
      state.billingPayment
        .outstandingInvoices,

    summary:
      state.billingPayment
        .outstandingSummary,

    loading:
      state.billingPayment
        .outstandingLoading,
  });

export const billingPaymentLoadingSelector = (
  state
) => ({
    listLoading:
      state.billingPayment
        .listLoading,

    detailsLoading:
      state.billingPayment
        .detailsLoading,

    summaryLoading:
      state.billingPayment
        .summaryLoading,

    createDataLoading:
      state.billingPayment
        .createDataLoading,

    dropdownsLoading:
      state.billingPayment
        .dropdownsLoading,

    outstandingLoading:
      state.billingPayment
        .outstandingLoading,

    createLoading:
      state.billingPayment
        .createLoading,

    updateLoading:
      state.billingPayment
        .updateLoading,

    cancelLoading:
      state.billingPayment
        .cancelLoading,

    deleteLoading:
      state.billingPayment
        .deleteLoading,
  });

export const billingPaymentOperationSelector = (
  state
) => ({
    success:
      state.billingPayment
        .operationSuccess,

    message:
      state.billingPayment
        .operationMessage,

    error:
      state.billingPayment
        .actionError,

    lastCreatedPayment:
      state.billingPayment
        .lastCreatedPayment,
  });

export default paymentSlice.reducer;